import React, { useMemo } from "react";
import Skeleton from "../../../components/Common/Skeleton";

/**
 * Calculates dynamic color classes based on remaining percentage.
 */
const getProgressColor = (percentage) => {
  if (percentage <= 40) {
    return {
      bar: "bg-red-500",
      dot: "bg-red-500",
      text: "text-red-500 dark:text-red-400",
    };
  }
  if (percentage <= 60) {
    return {
      bar: "bg-amber-500",
      dot: "bg-amber-500",
      text: "text-amber-500 dark:text-amber-400",
    };
  }
  return {
    bar: "bg-primary-bg", // Default primary green
    dot: "bg-primary-bg",
    text: "text-text-primary",
  };
};

const QuoteExpiryTimer = ({
  timeLeft = 0,
  totalDuration = 10,
  isExpired = false,
  loading = false,
  onRefresh,
}) => {
  // Calculate reverse percentage from dynamic total duration
  const progressPercentage = totalDuration > 0 ? (timeLeft / totalDuration) * 100 : 0;

  const colorClasses = useMemo(() => getProgressColor(progressPercentage), [progressPercentage]);

  // --- SKELETON LOADER STATE ---
  if (loading) {
    return (
      <Skeleton className="w-full animate-pulse space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-white/5 dark:bg-[#232624]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="h-2 w-2 rounded-full bg-gray-300 dark:bg-gray-600" />
            <Skeleton className="h-3 w-32 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
          <Skeleton className="h-3 w-6 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
        <Skeleton className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" />
      </Skeleton>
    );
  }

  // --- EXPIRED STATE ---
  if (isExpired) {
    return (
      <div className="w-full rounded-xl border border-red-500/30 bg-red-50 p-3.5 text-center transition-all duration-300 dark:border-red-500/20 dark:bg-red-500/10">
        <p className="text-sm font-medium text-red-600 dark:text-red-400">
          Quote has expired! Prices update frequently.
        </p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-green-600 hover:text-green-700 hover:underline focus:outline-none dark:text-emerald-400 dark:hover:text-emerald-300"
        >
          <svg
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh Quote
        </button>
      </div>
    );
  }

  // --- ACTIVE TIMER STATE ---
  return (
    <div className="w-full space-y-2 rounded-xl border border-divider bg-gray-50 p-3 font-outfit dark:border-white/5 dark:bg-[#232624]">
      <div className="flex items-center justify-between text-xs font-medium text-text-secondary">
        <span className="flex items-center gap-1.5">
          <span
            className={`h-2 w-2 rounded-full animate-pulse transition-colors duration-500 ${colorClasses.dot}`}
          />
          Quote guaranteed for:
        </span>
        <span className={`font-mono transition-colors duration-500 ${colorClasses.text}`}>
          {timeLeft}s
        </span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700/50">
        <div
          className={`h-full transition-all duration-1000 ease-linear ${colorClasses.bar}`}
          style={{ width: `${Math.min(100, Math.max(0, progressPercentage))}%` }}
        />
      </div>
    </div>
  );
};

export default QuoteExpiryTimer;